import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChartBarIcon, ClipboardDocumentCheckIcon, UserGroupIcon, DocumentCheckIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";

/**
 * HSE/KS Evaluation Overview - Evaluation overview page
 * Management/KS-responsible access - shows KPI, audits, management review
 */
export const HseKsEvaluationOverview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");
  const canViewEvaluation = useUserHasAccess({ role: "hse_ks_evaluation", action: "read" });
  const canViewAll = isAdmin || canViewEvaluation;

  // If user doesn't have access, show limited view or redirect
  if (!canViewAll) {
    return (
      <DashboardHeader
        title="Evaluering"
        subtitle="Begrenset tilgang - kontakt ledelse for full oversikt"
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <p className="text-gray-500">Du har ikke tilgang til evalueringsoversikt. Kontakt ledelse for mer informasjon.</p>
              </div>
            </div>
          </div>
        </div>
      </DashboardHeader>
    );
  }

  return (
    <DashboardHeader
      title="Evaluering"
      subtitle="KPI, internrevisjon, ledelsens gjennomgang og kundetilfredshet"
    >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* KPI Dashboard */}
            <div 
              className="bg-white shadow sm:rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate("/dashboard/hse-ks/evaluation/kpi")}
            >
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <ChartBarIcon className="h-8 w-8 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900 ml-3">KPI Dashboard</h2>
                </div>
                <p className="text-gray-500 text-sm">Oversikt over nøkkelindikatorer og målinger</p>
              </div>
            </div>

            {/* Internrevisjon */}
            <div 
              className="bg-white shadow sm:rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate("/dashboard/hse-ks/evaluation/audits")}
            >
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <ClipboardDocumentCheckIcon className="h-8 w-8 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900 ml-3">Internrevisjon</h2>
                </div>
                <p className="text-gray-500 text-sm">Oversikt over gjennomførte revisjoner</p>
              </div>
            </div>

            {/* Ledelsens gjennomgang */}
            <div 
              className="bg-white shadow sm:rounded-lg cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate("/dashboard/hse-ks/evaluation/management-review")}
            >
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <UserGroupIcon className="h-8 w-8 text-purple-600" />
                  <h2 className="text-lg font-semibold text-gray-900 ml-3">Ledelsens gjennomgang</h2>
                </div>
                <p className="text-gray-500 text-sm">Gjennomgang av systemets effektivitet</p>
              </div>
            </div>

            {/* Kundetilfredshet */}
            <div 
              className="bg-white shadow sm:rounded-lg cursor-pointer hover:shadow-md transition-shadow sm:col-span-2 lg:col-span-3"
              onClick={() => navigate("/dashboard/hse-ks/evaluation/customer-feedback")}
            >
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center mb-4">
                  <DocumentCheckIcon className="h-8 w-8 text-orange-600" />
                  <h2 className="text-lg font-semibold text-gray-900 ml-3">Kundetilfredshet</h2>
                </div>
                <p className="text-gray-500 text-sm">Oversikt over kundetilbakemeldinger og tilfredshet</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};

